import type React from 'react';
import { cloneElement, isValidElement } from 'react';
import { useFullscreenOverlay } from './FullscreenOverlayContext';
import type { FullscreenOverlayTriggerProps } from './types';
import { BELOW_LG } from '@/src/lib/breakpoints';

/**
 * FullscreenOverlayTrigger Component
 *
 * Trigger element that opens the fullscreen overlay when clicked.
 * Supports the `asChild` pattern from Constellation v10 for flexible composition.
 *
 * @example Default usage (renders a button)
 * <FullscreenOverlay.Trigger>Open Overlay</FullscreenOverlay.Trigger>
 *
 * @example With asChild (uses existing element)
 * <FullscreenOverlay.Trigger asChild>
 *   <button className="custom-button">Open Overlay</button>
 * </FullscreenOverlay.Trigger>
 */
export const FullscreenOverlayTrigger = ({ asChild = false, children }: FullscreenOverlayTriggerProps) => {
  const { open, triggerRef } = useFullscreenOverlay();

  // On mobile touch/pointer, prevent the browser from focusing the trigger.
  // This stops the auto-scroll that shifts window.scrollY before the portal
  // can capture it. Keyboard opens are not affected (no pointer event).
  const handlePointerDown = (event: React.PointerEvent) => {
    if (window.matchMedia(BELOW_LG).matches) {
      event.preventDefault();
    }
  };

  const handleOpen = (element: HTMLElement | null, keyboard: boolean) => {
    // Capture scroll position NOW, before React re-renders and the portal
    // effect runs. This is the earliest reliable point after user intent.
    triggerRef.current = { element, keyboard, scrollY: window.scrollY };
    open();
  };

  const handleClick = (event: React.MouseEvent) => {
    handleOpen(event.currentTarget as HTMLElement, false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen(event.currentTarget as HTMLElement, true);
    }
  };

  if (asChild && isValidElement(children)) {
    // Clone the child element and merge our props with existing props
    return cloneElement(children as React.ReactElement<Record<string, unknown>>, {
      onPointerDown: (event: React.PointerEvent) => {
        if (children.props.onPointerDown) {
          children.props.onPointerDown(event);
        }
        handlePointerDown(event);
      },
      onClick: (event: React.MouseEvent) => {
        if (children.props.onClick) {
          children.props.onClick(event);
        }
        handleClick(event);
      },
      onKeyDown: (event: React.KeyboardEvent) => {
        if (children.props.onKeyDown) {
          children.props.onKeyDown(event);
        }
        handleKeyDown(event);
      },
    });
  }

  // Default: render as button
  return (
    <button
      type="button"
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{
        background: 'none',
        border: 'none',

        padding: 0,
        font: 'inherit',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
};
